/**
 * Runtime application of a full store *design* (palette + structure).
 *
 * `applyStoreTheme.js` already injects a stylesheet that re-maps Tailwind
 * color utilities to `--st-*` variables inside `[data-store-theme]`. This
 * module layers the *structural* personality on top — a second injected
 * stylesheet that re-maps the radius ladder, swaps the font family and
 * softens shadows, plus card / cover / button variant tweaks keyed off
 * data attributes — all still scoped to the storefront subtree.
 *
 * Hard rule: this layer must never touch `position`, `z-index`, or the
 * layout of structural children (top bar, tabs, cover, floating cart).
 * The surface texture is therefore painted as the wrapper's *own*
 * background — never an overlay element — so it can't disturb stacking.
 *
 * The public storefront calls `getStoreDesignStyle(config)` for the inline
 * style (CSS-variable values + texture background) and
 * `getStoreDesignAttrs(config)` for the data attributes.
 */

import { getStoreThemeStyle } from './applyStoreTheme';
import { findStoreTheme } from './storeThemes';
import {
    resolveDesign, findFontPair, findRadius, findDensity, findMotion,
} from './storeDesign';

const STYLE_TAG_ID = 'store-design-overrides';

// Radius ladder we re-map. `rounded-full` / `rounded-pill` are intentionally
// excluded so avatars / qty pills / badges stay circular regardless of the
// corner choice.
const RADIUS_UTILS = [
    ['rounded',      '--st-r-md'],
    ['rounded-sm',   '--st-r-sm'],
    ['rounded-md',   '--st-r-md'],
    ['rounded-lg',   '--st-r-lg'],
    ['rounded-xl',   '--st-r-xl'],
    ['rounded-2xl',  '--st-r-2xl'],
    ['rounded-3xl',  '--st-r-3xl'],
];
const SHADOW_UTILS = ['shadow-soft', 'shadow-card', 'shadow-elevated', 'shadow-sm', 'shadow-md', 'shadow-lg', 'shadow'];

const buildDesignCSS = () => {
    const root = '[data-store-theme]';
    const lines = [];

    // ── Typography: body on the wrapper, display on every heading. ──
    lines.push(
        `${root}{font-family:var(--st-font-body),'Assistant',system-ui,sans-serif}`,
        `${root} h1,${root} h2,${root} h3,${root} h4,${root} .heading-xl,${root} .heading-lg,${root} .heading-md,${root} .heading-sm,${root} [data-store-name]{font-family:var(--st-font-display),'Assistant',system-ui,sans-serif;font-weight:var(--st-font-display-weight,800);letter-spacing:var(--st-font-display-tracking,-0.01em)}`,
    );

    // ── Corner language: re-map the radius ladder (border-radius only). ──
    for (const [util, varName] of RADIUS_UTILS) {
        lines.push(`${root} .${util}{border-radius:var(${varName})}`);
    }
    // Button shapes — scoped to elements that already look like buttons so
    // we never restyle arbitrary nodes.
    lines.push(
        `${root}[data-btn="pill"] button,${root}[data-btn="pill"] .btn{border-radius:9999px}`,
        `${root}[data-btn="sharp"] button,${root}[data-btn="sharp"] .btn{border-radius:0}`,
        `${root}[data-btn="outline"] button.bg-brand-500,${root}[data-btn="outline"] button.bg-mekomit-500,${root}[data-btn="outline"] .btn.bg-brand-500{background-color:transparent;color:var(--st-brand-700);box-shadow:inset 0 0 0 2px var(--st-brand-600)}`,
    );

    // ── Shadow temperament: one soft, palette-aware elevation. ──
    for (const s of SHADOW_UTILS) {
        lines.push(`${root} .${s}{box-shadow:var(--st-elevation)}`);
    }

    // ── Spatial density: contained to the product card body padding so it
    //    can never disturb the page shell / fixed regions. ──
    lines.push(
        `${root} [data-card-body]{padding:calc(0.75rem * var(--st-density,1))}`,
    );

    // ── Motion energy: hover lift + speed on product cards only. ──
    lines.push(
        `${root} [data-card-root]{transition:transform var(--st-dur,300ms) cubic-bezier(0.34,1.4,0.64,1),box-shadow var(--st-dur,300ms)}`,
        `${root}[data-motion="lively"] [data-card-root]:hover{transform:translateY(var(--st-lift,0)) scale(var(--st-lift-scale,1))}`,
    );

    // ── Card anatomy tweaks — keyed off the stable [data-card-*] hooks. ──
    lines.push(
        // minimal: dissolve frame + shadow, let products float
        `${root}[data-card="minimal"] [data-card-root]{box-shadow:none;background-color:transparent}`,
        // framed: museum frame
        `${root}[data-card="framed"] [data-card-root]{box-shadow:none;border:2px solid var(--st-line-strong);background-color:var(--st-surface)}`,
        // editorial: image bleeds, body becomes an overlay slab (contained
        // inside the card's own overflow-hidden box — no global impact)
        `${root}[data-card="editorial"] [data-card-root]{position:relative}`,
        `${root}[data-card="editorial"] [data-card-body]{position:absolute;inset-inline:0;bottom:0;background:linear-gradient(to top,rgba(0,0,0,0.82),rgba(0,0,0,0.15) 70%,transparent)}`,
        `${root}[data-card="editorial"] [data-card-body] *{color:#fff}`,
    );

    // ── Cover treatment — only resizes the cover's own media box. ──
    lines.push(
        `${root}[data-cover="minimal"] [data-store-cover-media]{height:5.5rem}`,
        `${root}[data-cover="banner"] [data-store-cover-media]{height:9rem}`,
        `${root}[data-cover="hero"] [data-store-cover-media]{height:18rem}`,
    );

    return lines.join('\n');
};

let injected = false;
const ensureDesignInjected = () => {
    if (injected || typeof document === 'undefined') return;
    if (document.getElementById(STYLE_TAG_ID)) { injected = true; return; }
    const el = document.createElement('style');
    el.id = STYLE_TAG_ID;
    el.textContent = buildDesignCSS();
    document.head.appendChild(el);
    injected = true;
};

/**
 * Texture as a *background* spec for the wrapper (never an overlay element).
 * Faintness is baked into the SVG so no extra layer / opacity is needed.
 */
const textureBackground = (id, ink) => {
    const c = (ink || '#1f1d1a').replace('#', '%23');
    switch (id) {
        case 'paper':
            return {
                image: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='2'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)' opacity='0.045'/%3E%3C/svg%3E")`,
                size: '160px 160px', repeat: 'repeat', attachment: 'scroll', blend: 'normal',
            };
        case 'dots':
            return {
                image: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='22' height='22'%3E%3Ccircle cx='2' cy='2' r='1.4' fill='${c}' fill-opacity='0.07'/%3E%3C/svg%3E")`,
                size: '22px 22px', repeat: 'repeat', attachment: 'scroll', blend: 'normal',
            };
        case 'grid':
            return {
                image: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='34' height='34'%3E%3Cpath d='M34 0H0V34' fill='none' stroke='${c}' stroke-opacity='0.06' stroke-width='1'/%3E%3C/svg%3E")`,
                size: '34px 34px', repeat: 'repeat', attachment: 'scroll', blend: 'normal',
            };
        case 'mesh':
            return {
                image: `radial-gradient(circle at 15% 12%, var(--st-brand-500) 0, transparent 38%), radial-gradient(circle at 85% 80%, var(--st-secondary-500) 0, transparent 40%)`,
                size: 'cover', repeat: 'no-repeat', attachment: 'fixed', blend: 'soft-light',
            };
        default:
            return null;
    }
};

/**
 * The inline `style` object for a storefront wrapper: palette variables
 * (delegated to applyStoreTheme) + every structural variable + the texture
 * background. Crucially adds NO positioning of any kind.
 */
export const getStoreDesignStyle = (input) => {
    ensureDesignInjected();
    const cfg = resolveDesign(input);
    const theme = findStoreTheme(cfg.themeId);
    const colorStyle = getStoreThemeStyle(cfg.themeId);

    const fp = findFontPair(cfg.fontPair);
    const rScale = findRadius(cfg.radius).scale;
    const dens = findDensity(cfg.density).factor;
    const mo = findMotion(cfg.motion);
    const tex = textureBackground(cfg.texture, theme?.tokens?.ink);

    const r = (rem) => `${(rem * rScale).toFixed(3)}rem`;

    const style = {
        ...colorStyle,
        '--st-font-display': fp.display,
        '--st-font-body': fp.body,
        '--st-font-display-weight': String(fp.displayWeight),
        '--st-font-display-tracking': fp.tracking,
        '--st-r-sm': r(0.45),
        '--st-r-md': r(0.7),
        '--st-r-lg': r(1.0),
        '--st-r-xl': r(1.35),
        '--st-r-2xl': r(1.7),
        '--st-r-3xl': r(2.2),
        '--st-density': String(dens),
        '--st-dur': mo.dur,
        '--st-lift': mo.lift,
        '--st-lift-scale': mo.scale,
        '--st-elevation': theme?.dark
            ? '0 10px 30px -8px rgba(0,0,0,0.55), 0 2px 8px -4px rgba(0,0,0,0.4)'
            : '0 10px 30px -10px rgba(31,29,26,0.16), 0 2px 8px -4px rgba(31,29,26,0.08)',
    };

    if (tex) {
        style.backgroundImage = tex.image;
        style.backgroundSize = tex.size;
        style.backgroundRepeat = tex.repeat;
        style.backgroundAttachment = tex.attachment;
        style.backgroundBlendMode = tex.blend;
    }

    return style;
};

/** Data attributes that toggle card / cover / button / motion / texture variants. */
export const getStoreDesignAttrs = (input) => {
    const cfg = resolveDesign(input);
    return {
        'data-store-theme': cfg.themeId,
        'data-card': cfg.card,
        'data-cover': cfg.cover,
        'data-btn': cfg.button,
        'data-motion': cfg.motion,
        'data-texture': cfg.texture,
    };
};
